import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import XIcon from 'icons/X.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { getIsArrayEmpty, getIsNodeFile } from 'utils';
import StageFileRow from '../StageFileRow';
import StageFilesEmptyState from '../StageFilesEmptyState';
import useStageNodeMeta from '../../hooks/useStageNodeMeta';
import useCheckedFilesField from '../../hooks/useCheckedFilesField';
import useOpenStageFile from '../../hooks/useOpenStageFile';
import { SelectedStageEntry, removeSelection } from '../../utils/stageFiles';

import './StageSelectedFiles.styles.scss';

type StageSelectedFilesProps = {
  className?: string;
  entries: SelectedStageEntry[];
  stage: string;
  hasFiles: boolean;
};

const StageSelectedFiles: React.FC<StageSelectedFilesProps> = (props) => {
  const { className, entries, stage, hasFiles } = props;

  const { checkedFilesIds, setCheckedFilesIds } = useCheckedFilesField();

  const { t } = useTranslation();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const { getEntryMeta } = useStageNodeMeta();

  const openStageFile = useOpenStageFile();

  const classes = classNames('stage-selected-files', className);

  // Folder cards stay plain; prev and next in the preview walk the files.
  const selectedFiles = entries.map(({ node }) => node).filter(getIsNodeFile);

  if (getIsArrayEmpty(entries)) {
    return (
      <StageFilesEmptyState
        title={t('nothingSelectedYet')}
        description={
          !hasFiles
            ? t('nothingSelectedNoFilesDescription')
            : isAbovePhablet
            ? t('nothingSelectedDescription', { stage })
            : t('nothingSelectedMobileDescription', { stage })
        }
      />
    );
  }

  return (
    <ul className={classes}>
      {entries.map((entry) => (
        <li key={entry.node._id}>
          <StageFileRow
            node={entry.node}
            meta={getEntryMeta(entry)}
            action={
              <Button
                type="button"
                variant="icon"
                icon={XIcon}
                className="stage-selected-files__remove"
                aria-label={t('remove')}
                disabled={getIsArrayEmpty(entry.removableIds)}
                onClick={() =>
                  setCheckedFilesIds(
                    removeSelection(checkedFilesIds, entry.removableIds),
                  )
                }
              />
            }
            {...(getIsNodeFile(entry.node) && {
              onNameClick: () => openStageFile(entry.node, selectedFiles),
            })}
          />
        </li>
      ))}
    </ul>
  );
};

export default StageSelectedFiles;
